import { Alert, Button, Popconfirm } from 'antd';
import { ConfirmItsYouPanel } from '../../../components/ConfirmItsYouPanel';
import { usePassword } from '../hooks/use-password';
import { PasswordForm } from './PasswordForm';
import { SecurityCard } from './SecurityCard';
import { StatusBadge } from './StatusBadge';

function PasswordActions({ hasPassword, password }: { hasPassword: boolean; password: ReturnType<typeof usePassword> }) {
  if (!hasPassword) {
    return (
      <Button className="mt-4" type="primary" onClick={password.openForm}>
        Set a password
      </Button>
    );
  }
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <Button onClick={password.openForm}>Change password</Button>
      <Popconfirm
        title="Remove your password?"
        description="You'll sign in with a code instead. Two-factor turns off with it."
        okText="Remove"
        okButtonProps={{ danger: true }}
        onConfirm={password.remove}
      >
        <Button danger loading={password.submitting}>
          Remove password
        </Button>
      </Popconfirm>
    </div>
  );
}

/** A password is optional: codes are the way in, and a password is a second way that two-factor then guards. */
export function PasswordCard({ hasPassword }: { hasPassword: boolean }) {
  const password = usePassword(hasPassword);
  const isConfirming = password.confirmation.state.phase === 'code';

  return (
    <SecurityCard
      title="Password"
      badge={<StatusBadge isOn={hasPassword} label={hasPassword ? 'Set' : 'Not set'} />}
      description={
        hasPassword
          ? 'You can sign in with your email and password instead of a code. Two-factor below guards it.'
          : "You don't need one. Set a password if you'd like to sign in without waiting for a code."
      }
    >
      {password.error && <Alert className="mt-4" type="error" title={password.error} showIcon />}
      <ConfirmItsYouPanel confirmation={password.confirmation} />
      {!isConfirming && password.isFormOpen && (
        <PasswordForm
          submitLabel={hasPassword ? 'Change password' : 'Set password'}
          submitting={password.submitting}
          onSave={password.save}
          onCancel={password.closeForm}
        />
      )}
      {!isConfirming && !password.isFormOpen && <PasswordActions hasPassword={hasPassword} password={password} />}
    </SecurityCard>
  );
}
